// ─────────────────────────────────────────────────────────────────────────────
// StepParksLodgesExperiences.jsx  —  Step 02 (Final, 7-step flow)
//
// CHANGES FROM PREVIOUS VERSION
// ──────────────────────────────
// - Removed hardcoded SERENGETI_MIGRATION_ZONES constant entirely.
// - Serengeti zones are now fetched from the DB via GET_SERENGETI_ZONES query
//   inside CountryParkSection when a Serengeti park is selected.
// - Zone keywords used for lodge filtering come directly from DB
//   locationKeywords — same source the backend park_fee_service.py uses for
//   _resolve_migration_zone(), so frontend filtering and backend tariff
//   matching are always in sync.
// - ZONE_UI_META provides icon/color/months display metadata keyed by the
//   first word of zoneName (e.g. "Northern Serengeti" → key "northern").
// - SerengetiLodgeSection now receives `migrationZones` prop (DB-driven)
//   instead of referencing the old hardcoded constant.
// - All other logic (Zanzibar zones, proximity groups, park chips, experiences)
//   is unchanged.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo, useCallback ,  useRef } from 'react';
import { useQuery, useLazyQuery, useApolloClient} from '@apollo/client/react';
import {
  ArrowLeft, ArrowRight, Check, ChevronDown, ChevronUp,
  MapPin, Mountain, Zap, Camera, Waves, Building2,
  Utensils, Heart, Sparkles, Clock, Info, Plus, Minus,
  Trees, Navigation, Star, Tent, Fish, Globe, Hotel,
} from 'lucide-react';
import {
  GET_PARKS_BY_COUNTRY,
  GET_PROPERTIES_BY_PARK,
  GET_EXPERIENCES_BY_COUNTRY,
  GET_SERENGETI_ZONES,
  GET_GREAT_MIGRATION,
} from '../graphql/tourBuilder';

/* ─── Design tokens ─── */
const GOLD     = '#B8975A';
const GOLD_DIM = 'rgba(184,151,90,0.15)';
const NAVY     = '#080e1a';

const COUNTRY_ACCENT = {
  tanzania: '#c2813a', kenya: '#b85c38', italy: '#2d6a4f',
  france:   '#003087', japan: '#9b2335', iceland: '#1a5276',
  zanzibar: '#1a7a6e',
};
const getCountryColor = (name) =>
  COUNTRY_ACCENT[(name || '').toLowerCase()] || GOLD;

const CATEGORY_ICONS = {
  wildlife: Mountain, nature: Mountain, adventure: Zap,
  cultural: Building2, gastronomy: Utensils, leisure: Waves,
  wellness: Heart, photography: Camera, personal: Sparkles,
};

// ─────────────────────────────────────────────────────────────────────────────
// ZONE_UI_META
// Provides display-only metadata (icon, color, months label) for each
// migration zone. Keyed by the lowercased first word of the DB zone_name.
// The actual keywords and season_type come from the DB via GET_SERENGETI_ZONES.
// ─────────────────────────────────────────────────────────────────────────────
// Icons only — WHEN the herds are in a zone comes from the Great Migration
// calendar (greatMigration query), never hard-coded here: the old labels
// named zones the data doesn't use and called "central" year-round.
const ZONE_UI_META = {
  ndutu:     { icon: Tent,       color: GOLD },
  southern:  { icon: Tent,       color: GOLD },
  seronera:  { icon: Mountain,   color: GOLD },
  grumeti:   { icon: Waves,      color: GOLD },
  western:   { icon: Waves,      color: GOLD },
  kogatende: { icon: Navigation, color: GOLD },
  northern:  { icon: Navigation, color: GOLD },
  eastern:   { icon: Mountain,   color: GOLD },
};
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/* Months a zone holds the herds, as wrap-aware ranges: [12,1,2,3] → "Dec–Mar". */
function monthRanges(months) {
  if (!months.length) return '';
  if (months.length === 12) return 'Year-round';
  const set = new Set(months);
  // start a run where the previous month is absent
  const starts = months.filter((m) => !set.has(m === 1 ? 12 : m - 1));
  return starts.map((start) => {
    let end = start;
    while (set.has(end === 12 ? 1 : end + 1) && (end === 12 ? 1 : end + 1) !== start) end = end === 12 ? 1 : end + 1;
    return start === end ? MONTH_ABBR[start - 1] : `${MONTH_ABBR[start - 1]}–${MONTH_ABBR[end - 1]}`;
  }).join(', ');
}

// Derive a stable UI key from a DB zone_name string.
// e.g. "Northern Serengeti & Mara" → "northern"
const zoneNameToKey = (zoneName = '') => zoneName.toLowerCase().split(/[\s_&]/)[0];

/* ─── Zanzibar geographic zones ─── */
const ZANZIBAR_ZONES = [
  { key: 'north',      label: 'Nungwi & North Coast',  sub: 'Iconic white sand, year-round swimming, vibrant village',     icon: Fish,      color: '#06b6d4', keywords: ['nungwi', 'kendwa', 'north', 'tazari'] },
  { key: 'east',       label: 'Paje & East Coast',      sub: 'Kite surfing, tide pools, quieter beaches with rustic charm', icon: Waves,     color: '#8b5cf6', keywords: ['paje', 'bwejuu', 'jambiani', 'east', 'kite'] },
  { key: 'stone_town', label: 'Stone Town & West',      sub: 'UNESCO old town, spice tours, sunset dhow cruises',           icon: Building2, color: '#d97706', keywords: ['stone town', 'west', 'forodhani', 'old town'] },
  { key: 'south',      label: 'South & Kizimkazi',      sub: 'Wild dolphin encounters, secluded coves, eco lodges',         icon: Globe,     color: '#10b981', keywords: ['south', 'kizimkazi', 'fumba', 'menai'] },
  { key: 'pemba',      label: 'Pemba Island',            sub: 'Remote, lush, world-class diving — the forgotten island',     icon: Trees,     color: '#6366f1', keywords: ['pemba', 'wete', 'chake'] },
];

/* ─── Proximity groups ─── */
const PROXIMITY_GROUPS = [
  { key: 'inside_park', label: 'Inside the Reserve', color: '#4ade80' },
  { key: 'park_border', label: 'On the Border',       color: GOLD     },
  { key: 'nearby',      label: 'Within 1hr Drive',    color: '#12100e' },
];

/* ─── Park type helpers ─── */
const isSerengeti = (park) =>
  (park?.slug || '').includes('serengeti') ||
  (park?.name || '').toLowerCase().includes('serengeti');

const isZanzibar = (park) =>
  (park?.slug || '').includes('zanzibar') ||
  (park?.name || '').toLowerCase().includes('zanzibar');

/* ─── Map API property → UI shape ─── */
const mapApiProperty = (prop) => {
  const cheapest = prop.roomTypes?.length
    ? [...prop.roomTypes].sort((a, b) => a.pricePerNight - b.pricePerNight)[0]
    : null;
  let amenities = prop.amenities || [];
  if (typeof amenities === 'string') { try { amenities = JSON.parse(amenities); } catch { amenities = []; } }
  return {
    id:             prop.id,
    name:           prop.name,
    slug:           prop.slug,
    tier:           [prop.levelLabel, prop.propertyType?.name].filter(Boolean).join(' · '),
    tierSlug:       prop.level || '',
    stars:          Math.round(parseFloat(prop.averageRating) || 0),
    desc:           prop.shortDescription || '',
    image:          prop.featuredImage || '',
    price:          cheapest ? parseFloat(cheapest.pricePerNight) : 0,
    unit:           '/room/night',
    perks:          Array.isArray(amenities) ? amenities.slice(0, 5) : [],
    featured:       prop.isFeatured || false,
    locationType:   (prop.locationType || '').toLowerCase(),
    parkId:         prop.park?.id   || null,
    parkName:       prop.park?.name || '',
    locationDetail: prop.locationDetail || prop.location || '',
    dbId:           prop.id,
  };
};

const mapApiExperience = (exp) => ({
  id:             exp.id,
  name:           exp.name,
  icon:           CATEGORY_ICONS[exp.category?.slug] || Mountain,
  category:       exp.category?.name || 'Experience',
  categorySlug:   exp.category?.slug || '',
  price:          parseFloat(exp.pricePerPerson) || 0,
  duration:       exp.durationDisplay || '',
  difficulty:     exp.difficulty
    ? exp.difficulty.charAt(0).toUpperCase() + exp.difficulty.slice(1).toLowerCase()
    : 'Easy',
  desc:           exp.shortDescription || exp.description || '',
  parkSlug:       exp.park?.slug || '',
  parkName:       exp.park?.name || '',
  parkId:         exp.park?.id   || null,
  priceOnRequest: exp.priceOnRequest || false,
  dbId:           exp.id,
});

/* ══════════════════════════════════════
   ACCOMMODATION CARD
══════════════════════════════════════ */
const AccommodationCard = ({ opt, isSelected, onSelect, accentColor, nights }) => (
  <button
    onClick={() => onSelect(opt.id)}
    style={{
      border: `1px solid ${isSelected ? accentColor : '#e2ddd6'}`,
      background: isSelected ? `${accentColor}18` : '#fff',
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
      transform: isSelected ? 'translateY(-3px)' : 'none',
      boxShadow: isSelected ? `0 10px 32px ${accentColor}18` : 'none',
      textAlign: 'left', overflow: 'hidden', position: 'relative', width: '100%',
    }}
  >
    {opt.featured && (
      <div style={{
        position: 'absolute', top: 0, right: 0, background: GOLD, color: NAVY,
        fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 700,
        letterSpacing: '0.16em', textTransform: 'uppercase', padding: '4px 10px', zIndex: 2,
      }}>Most Popular</div>
    )}
    <div style={{
      aspectRatio: '16/9', backgroundSize: 'cover', backgroundPosition: 'center',
      backgroundImage: opt.image ? `url(${opt.image})` : 'none',
      backgroundColor: !opt.image ? '#f0ece6' : 'transparent',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.35) 100%)' }} />
      {isSelected && (
        <div style={{
          position: 'absolute', top: '8px', left: '8px', zIndex: 2,
          width: '24px', height: '24px', background: accentColor,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Check size={11} strokeWidth={2.5} color="#fff" />
        </div>
      )}
      {opt.tier && (
        <div style={{ position: 'absolute', bottom: '8px', left: '12px', zIndex: 2 }}>
          <span style={{
            fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '0.16em', padding: '3px 8px',
            background: isSelected ? accentColor : 'rgba(0,0,0,0.5)', color: '#fff',
          }}>{opt.tier}</span>
        </div>
      )}
      {opt.stars > 0 && (
        <div style={{ position: 'absolute', bottom: '8px', right: '12px', zIndex: 2 }}>
          <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', color: GOLD }}>
            {'★'.repeat(Math.min(opt.stars, 5))}
          </span>
        </div>
      )}
    </div>
    <div style={{ padding: '20px' }}>
      <h4 style={{
        fontFamily: 'Cormorant Garamond, serif', fontSize: '25px', fontWeight: 300,
        fontStyle: 'normal', color: '#0f172a', lineHeight: 1, marginBottom: '4px',
      }}>{opt.name}</h4>
      <p style={{
        fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 300, color: '#12100e',
        lineHeight: 1.5, marginBottom: '14px',
      }}>{opt.desc}</p>
      {opt.perks?.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          {opt.perks.slice(0, 3).map(p => (
            <div key={p} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
              <div style={{ width: '4px', height: '4px', borderRadius: '50%', flexShrink: 0, background: isSelected ? accentColor : '#cbd5e1' }} />
              <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', color: '#12100e' }}>{p}</span>
            </div>
          ))}
          {opt.perks.length > 3 && (
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', color: accentColor, marginTop: '4px' }}>
              +{opt.perks.length - 3} more inclusions
            </p>
          )}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', paddingTop: '12px', borderTop: '1px solid #f0ece6' }}>
        <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '32px', fontWeight: 300, color: '#0f172a', lineHeight: 1 }}>
          ${opt.price.toLocaleString()}
        </span>
        <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', color: '#12100e' }}>{opt.unit}</span>
        {opt.price > 0 && nights > 0 && (
          <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', color: '#12100e', marginLeft: 'auto' }}>
            ≈ ${(opt.price * nights).toLocaleString()} for {nights}n
          </span>
        )}
      </div>
    </div>
  </button>
);

/* ── Proximity-grouped AccommodationCards ── */
const ProximityGroupedCards = ({ props, selectedId, onSelect, accentColor, nights }) => {
  const hasTypes = props.some(p => p.locationType && p.locationType !== '');

  if (!hasTypes) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
        {props.map(opt => (
          <AccommodationCard key={opt.id} opt={opt}
            isSelected={selectedId === opt.id}
            onSelect={onSelect} accentColor={accentColor} nights={nights} />
        ))}
      </div>
    );
  }

  return (
    <>
      {PROXIMITY_GROUPS.map(grp => {
        const grpProps = props.filter(p => p.locationType === grp.key);
        if (!grpProps.length) return null;
        return (
          <div key={grp.key} style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: grp.color }} />
              <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.16em', color: grp.color }}>
                {grp.label}
              </span>
              <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', padding: '1px 6px', background: `${grp.color}18`, border: `1px solid ${grp.color}40`, color: grp.color }}>
                {grpProps.length}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
              {grpProps.map(opt => (
                <AccommodationCard key={opt.id} opt={opt}
                  isSelected={selectedId === opt.id}
                  onSelect={onSelect} accentColor={grp.color} nights={nights} />
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
};

/* ══════════════════════════════════════
   SERENGETI LODGE SECTION
   Receives migrationZones from DB (via GET_SERENGETI_ZONES).
   Keywords used for lodge filtering are the same DB locationKeywords
   that park_fee_service.py uses for zone matching — single source of truth.
══════════════════════════════════════ */
const SerengetiLodgeSection = ({
  park, allLodges, nights,
  migrationZones,                    // DB-driven zone list — replaces old hardcoded constant
  migrationsLoading,
  herdMonth = null,                  // where the herds are in the guest's month (calendar)
  monthName = null,
  selectedSerengetiZones, onSerengetiZonesChange,
  selectedId, onSelect, accentColor,
}) => {
  const intentChosen = selectedSerengetiZones !== undefined;
  const intentYes    = intentChosen && Array.isArray(selectedSerengetiZones) && selectedSerengetiZones.length > 0;
  const intentNo     = intentChosen && Array.isArray(selectedSerengetiZones) && selectedSerengetiZones.length === 0;
  const selectedZoneKeys = Array.isArray(selectedSerengetiZones) ? selectedSerengetiZones.map(z => z.key) : [];

  const toggleZone = (zone) => {
    const next = selectedZoneKeys.includes(zone.key)
      ? selectedZoneKeys.filter(k => k !== zone.key)
      : [...selectedZoneKeys, zone.key];
    onSerengetiZonesChange(migrationZones.filter(z => next.includes(z.key)));
  };

  // Lodge filtering uses DB locationKeywords — same as backend _resolve_migration_zone()
  const lodgesForZone = (zoneKey) => {
    const zone = migrationZones.find(z => z.key === zoneKey);
    if (!zone) return [];
    return allLodges.filter(l => {
      const text = [l.name, l.desc, l.locationDetail].join(' ').toLowerCase();
      return zone.keywords.some(kw => text.includes(kw.toLowerCase()));
    });
  };

  return (
    <div>
      {/* Intent question */}
      <div style={{ border: '1px solid rgba(184,151,90,0.3)', background: 'rgba(184,151,90,0.04)', padding: '20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <Navigation size={14} strokeWidth={1.5} style={{ color: GOLD }} />
          <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.2em', color: GOLD }}>
            Migration Intent
          </span>
        </div>
        <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '25px', fontStyle: 'normal', fontWeight: 300, color: '#0f172a', marginBottom: '8px', lineHeight: 1.2 }}>
          Are you planning to follow the Great Migration?
        </p>
        <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 300, color: '#12100e', lineHeight: 1.5, marginBottom: '16px' }}>
          The wildebeest migration moves through different Serengeti zones by season.
          Tick one or more zones to see matched lodges, or choose any Serengeti lodge.
        </p>
        {herdMonth && monthName && (
          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '14px', color: '#3d3326', lineHeight: 1.6, margin: '0 0 16px', padding: '10px 12px', background: '#fff', borderLeft: `2px solid ${GOLD}` }}>
            <strong style={{ fontWeight: 600 }}>In {monthName}</strong> the herds are usually around {herdMonth.where}: {herdMonth.moment}.
          </p>
        )}
        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { val: true,  label: 'Yes — follow the migration' },
            { val: false, label: 'No — any Serengeti lodge'   },
          ].map(({ val, label }) => {
            const isActive = val ? intentYes : intentNo;
            return (
              <button key={String(val)}
                onClick={() => {
                  if (val === false) onSerengetiZonesChange([]);
                  else onSerengetiZonesChange(undefined);
                }}
                style={{
                  flex: 1, padding: '10px 14px',
                  border: `1px solid ${isActive ? GOLD : '#e2ddd6'}`,
                  background: isActive ? GOLD_DIM : '#fff',
                  fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.16em',
                  color: isActive ? GOLD : '#64748b',
                  cursor: 'pointer', transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}>
                {isActive && <Check size={10} strokeWidth={2.5} />}
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Zone multi-select (when "yes migration" or not yet answered) */}
      {!intentNo && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ width: '24px', height: '1px', background: GOLD }} />
            <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.2em', color: GOLD }}>
              Select Migration Zone(s)
            </span>
          </div>
          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 300, color: '#12100e', marginBottom: '12px', lineHeight: 1.5 }}>
            Tick one or more — lodges matched to each selected zone appear below.
          </p>

          {migrationsLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {[1, 2, 3, 4].map(i => (
                <div key={i} style={{ height: '80px', background: 'linear-gradient(90deg, #f0ece6 25%, #e8e4de 50%, #f0ece6 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
              ))}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '8px', marginBottom: '16px' }}>
              {migrationZones.map(zone => {
                const ZIcon     = zone.icon;
                const isZoneSel = selectedZoneKeys.includes(zone.key);
                const count     = lodgesForZone(zone.key).length;
                return (
                  <button key={zone.key} onClick={() => toggleZone(zone)}
                    style={{
                      textAlign: 'left', padding: '14px',
                      border: `1px solid ${isZoneSel ? zone.color : '#e2ddd6'}`,
                      background: isZoneSel ? `${zone.color}10` : '#fff',
                      cursor: 'pointer', transition: 'all 0.25s', position: 'relative', overflow: 'hidden',
                    }}>
                    {isZoneSel && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '3px', background: zone.color }} />}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ZIcon size={13} strokeWidth={1.5} style={{ color: zone.color }} />
                        <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.06em', color: '#3d3326' }}>{zone.months}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {count > 0 && (
                          <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', padding: '2px 6px', background: `${zone.color}18`, border: `1px solid ${zone.color}40`, color: zone.color }}>
                            {count} lodge{count !== 1 ? 's' : ''}
                          </span>
                        )}
                        <div style={{ width: '16px', height: '16px', border: `1px solid ${isZoneSel ? zone.color : '#ddd9d2'}`, background: isZoneSel ? zone.color : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {isZoneSel && <Check size={8} strokeWidth={3} color="#fff" />}
                        </div>
                      </div>
                    </div>
                    <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '21px', fontStyle: 'normal', fontWeight: 300, color: '#0f172a', lineHeight: 1.1, marginBottom: '4px' }}>{zone.label}</p>
                    {monthName && (
                      <span style={{
                        display: 'inline-block', marginBottom: '6px', fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 600,
                        letterSpacing: '0.1em', textTransform: 'uppercase', padding: '2px 8px',
                        border: `1px solid ${zone.herd ? 'rgba(184,151,90,.5)' : '#e2ddd6'}`,
                        background: zone.herd ? 'rgba(184,151,90,.1)' : '#faf8f4',
                        color: zone.herd ? '#8a6a2f' : '#8a8378',
                      }}>
                        {zone.herd === 'peak' ? `Herds at their peak in ${monthName}` : zone.herd ? `Herds here in ${monthName}` : `No herds in ${monthName}`}
                      </span>
                    )}
                    <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 300, color: '#12100e', lineHeight: 1.4 }}>{zone.sub}</p>
                  </button>
                );
              })}
            </div>
          )}

          {/* Dates vs. herds: the guest wants the migration but the zones
              they ticked won't have it in their month. */}
          {(() => {
            if (!monthName || !selectedZoneKeys.length) return null;
            const chosen = migrationZones.filter((z) => selectedZoneKeys.includes(z.key));
            if (chosen.some((z) => z.herd)) return null;
            const right = migrationZones.find((z) => z.herd);
            return (
              <div style={{ margin: '0 0 16px', padding: '12px 14px', border: '1px solid rgba(180,90,74,.3)', background: 'rgba(180,90,74,.05)', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <p style={{ flex: '1 1 260px', margin: 0, fontFamily: 'Jost, sans-serif', fontSize: '14px', lineHeight: 1.6, color: '#3d3326' }}>
                  The herds usually aren't in {chosen.map((z) => z.label).join(' or ')} in {monthName}
                  {herdMonth ? ` — they're around ${herdMonth.where}.` : '.'}
                </p>
                {right && (
                  <button type="button" onClick={() => onSerengetiZonesChange([right])}
                    style={{ padding: '9px 14px', border: `1px solid ${GOLD}`, background: GOLD, color: '#080e1a', cursor: 'pointer',
                      fontFamily: 'Jost, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                    Switch to {right.label}
                  </button>
                )}
              </div>
            );
          })()}

          {/* Per-zone lodge accordions */}
          {selectedZoneKeys.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {selectedZoneKeys.map(zoneKey => {
                const zone       = migrationZones.find(z => z.key === zoneKey);
                if (!zone) return null;
                const zoneLodges = lodgesForZone(zoneKey);
                const showLodges = zoneLodges.length > 0 ? zoneLodges : allLodges;
                const isFallback = zoneLodges.length === 0;
                return (
                  <div key={zoneKey} style={{ border: `1px solid ${zone.color}50`, overflow: 'hidden' }}>
                    <div style={{ padding: '10px 16px', background: `${zone.color}0c`, borderBottom: `1px solid ${zone.color}25`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <MapPin size={10} strokeWidth={1.5} style={{ color: zone.color }} />
                        <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.16em', color: zone.color }}>{zone.label}</span>
                        {isFallback && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', color: '#12100e' }}>· showing all Serengeti options</span>}
                      </div>
                      <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', color: zone.color }}>
                        {showLodges.length} propert{showLodges.length !== 1 ? 'ies' : 'y'}
                      </span>
                    </div>
                    <div style={{ padding: '16px' }}>
                      {showLodges.length === 0 ? (
                        <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', color: '#12100e', fontStyle: 'normal' }}>
                          No lodges tagged for this zone yet — our team will suggest options.
                        </p>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
                          {showLodges.map(opt => (
                            <AccommodationCard key={opt.id} opt={opt}
                              isSelected={selectedId === opt.id}
                              onSelect={onSelect} accentColor={zone.color} nights={nights} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ border: '1px dashed rgba(184,151,90,0.3)', padding: '16px', textAlign: 'center' }}>
              <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', color: '#12100e', fontWeight: 300 }}>
                Tick one or more migration zones above to see matched lodges
              </p>
            </div>
          )}
        </div>
      )}

      {/* "No migration" — all Serengeti lodges */}
      {intentNo && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ width: '24px', height: '1px', background: GOLD }} />
            <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.2em', color: GOLD }}>All Serengeti Properties</span>
          </div>
          {allLodges.length === 0 ? (
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', color: '#12100e', fontStyle: 'normal' }}>
              No lodges on file yet — our team will source options.
            </p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
              {allLodges.map(opt => (
                <AccommodationCard key={opt.id} opt={opt}
                  isSelected={selectedId === opt.id}
                  onSelect={onSelect} accentColor={accentColor} nights={nights} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════
   ZANZIBAR LODGE SECTION
══════════════════════════════════════ */
const ZanzibarLodgeSection = ({ allLodges, nights, selectedId, onSelect, accentColor }) => {
  const [activeZoneKey, setActiveZoneKey] = useState(null);

  const grouped = useMemo(() => {
    const map = {};
    const unzoned = [];
    allLodges.forEach(prop => {
      const text = [prop.name, prop.desc, prop.locationDetail, prop.parkName].join(' ').toLowerCase();
      const zone = ZANZIBAR_ZONES.find(z => z.keywords.some(kw => text.includes(kw)));
      if (zone) {
        if (!map[zone.key]) map[zone.key] = { zone, props: [] };
        map[zone.key].props.push(prop);
      } else {
        unzoned.push(prop);
      }
    });
    return { byZone: Object.values(map), unzoned };
  }, [allLodges]);

  const activeZone    = ZANZIBAR_ZONES.find(z => z.key === activeZoneKey);
  const activeGroup   = grouped.byZone.find(g => g.zone.key === activeZoneKey);
  const displayLodges = activeZoneKey
    ? (activeGroup?.props.length ? activeGroup.props : allLodges)
    : null;

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
        <button
          onClick={() => setActiveZoneKey(null)}
          style={{
            padding: '7px 14px',
            border: `1px solid ${!activeZoneKey ? GOLD : '#e2ddd6'}`,
            background: !activeZoneKey ? GOLD_DIM : '#fff',
            fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '0.16em',
            color: !activeZoneKey ? GOLD : '#94a3b8', cursor: 'pointer', transition: 'all 0.2s',
          }}>All zones</button>
        {ZANZIBAR_ZONES.map(zone => {
          const ZIcon    = zone.icon;
          const isActive = activeZoneKey === zone.key;
          return (
            <button key={zone.key} onClick={() => setActiveZoneKey(isActive ? null : zone.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px',
                border: `1px solid ${isActive ? zone.color : '#e2ddd6'}`,
                background: isActive ? `${zone.color}12` : '#fff',
                fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.14em',
                color: isActive ? zone.color : '#64748b', cursor: 'pointer', transition: 'all 0.2s',
              }}>
              <ZIcon size={10} strokeWidth={1.5} />{zone.label}
            </button>
          );
        })}
      </div>

      {activeZone && (
        <div style={{ padding: '12px 16px', border: `1px solid ${activeZone.color}40`, background: `${activeZone.color}08`, display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
          {(() => { const ZIcon = activeZone.icon; return <ZIcon size={16} strokeWidth={1.5} style={{ color: activeZone.color, flexShrink: 0 }} />; })()}
          <div>
            <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '21px', fontStyle: 'normal', fontWeight: 300, color: '#0f172a', lineHeight: 1.1, marginBottom: '2px' }}>{activeZone.label}</p>
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', color: '#12100e', fontWeight: 300 }}>{activeZone.sub}</p>
          </div>
        </div>
      )}

      {activeZoneKey ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
          {(displayLodges || []).map(opt => (
            <AccommodationCard key={opt.id} opt={opt}
            isSelected={String(selectedId) === String(opt.id)}

              onSelect={onSelect} accentColor={accentColor} nights={nights} />
          ))}
        </div>
      ) : (
        <>
          {grouped.byZone.map(({ zone, props }) => {
            const ZIcon = zone.icon;
            return (
              <div key={zone.key} style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <ZIcon size={11} strokeWidth={1.5} style={{ color: zone.color }} />
                  <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.16em', color: zone.color }}>{zone.label}</span>
                  <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 600, padding: '1px 6px', background: `${zone.color}18`, border: `1px solid ${zone.color}40`, color: zone.color }}>{props.length}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
                  {props.map(opt => (
                    <AccommodationCard key={opt.id} opt={opt}
                      isSelected={String(selectedId) === String(opt.id)}
                      onSelect={onSelect} accentColor={accentColor} nights={nights} />
                  ))}
                </div>
              </div>
            );
          })}
          {grouped.unzoned.length > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <MapPin size={11} strokeWidth={1.5} style={{ color: '#12100e' }} />
                <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.16em', color: '#12100e' }}>Other</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
                {grouped.unzoned.map(opt => (
                  <AccommodationCard key={opt.id} opt={opt}
                    isSelected={String(selectedId) === String(opt.id)}
                    onSelect={onSelect} accentColor={accentColor} nights={nights} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ══════════════════════════════════════
   PARK CHIP
══════════════════════════════════════ */
const ParkChip = ({ park, isSelected, onToggle, accentColor }) => (
  <button
    onClick={() => onToggle(park)}
    style={{
      display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px',
      border: `1px solid ${isSelected ? accentColor : '#e2ddd6'}`,
      background: isSelected ? `${accentColor}18` : '#fff',
      cursor: 'pointer', transition: 'all 0.22s cubic-bezier(0.22,1,0.36,1)',
      transform: isSelected ? 'translateY(-1px)' : 'none',
      boxShadow: isSelected ? `0 4px 16px ${accentColor}20` : 'none',
      position: 'relative', overflow: 'hidden',
    }}
  >
    {park.isFeatured && <div style={{ position: 'absolute', top: 0, left: 0, width: '3px', height: '100%', background: accentColor }} />}
    <div style={{
      width: '36px', height: '36px', flexShrink: 0,
      backgroundImage: park.featuredImage ? `url(${park.featuredImage})` : 'none',
      backgroundColor: !park.featuredImage ? '#f0ece6' : 'transparent',
      backgroundSize: 'cover', backgroundPosition: 'center',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {!park.featuredImage && <Mountain size={14} strokeWidth={1.5} style={{ color: '#12100e' }} />}
    </div>
    <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
      <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '18px', fontWeight: 300, fontStyle: 'normal', color: '#0f172a', lineHeight: 1.1, marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{park.name}</p>
      {park.parkType?.name && (
        <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.16em', color: isSelected ? accentColor : '#94a3b8' }}>{park.parkType.name}</p>
      )}
    </div>
    <div style={{ width: '18px', height: '18px', flexShrink: 0, border: `1px solid ${isSelected ? accentColor : '#ddd9d2'}`, background: isSelected ? accentColor : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
      {isSelected && <Check size={9} strokeWidth={3} color="#fff" />}
    </div>
  </button>
);

/* ── Park nights splitter ── */
const ParkNightsSplitter = ({ selectedParksList, parkNights, totalNights, onChange }) => {
  const totalAllocated = selectedParksList.reduce((s, p) => s + (parkNights[p.id] || 1), 0);
  return (
    <div style={{ border: '1px solid rgba(184,151,90,0.25)', background: 'rgba(184,151,90,0.04)', padding: '16px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.2em', color: GOLD }}>Allocate Nights</span>
        <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '22px', fontWeight: 300, color: totalAllocated === totalNights ? '#0f172a' : '#f87171' }}>
          {totalAllocated} <span style={{ fontSize: '15px', color: '#12100e' }}>of {totalNights}</span>
        </span>
      </div>
      {selectedParksList.map(park => {
        const nights = parkNights[park.id] || 1;
        const max    = totalNights - (selectedParksList.length - 1);
        const pct    = Math.round((nights / totalNights) * 100);
        return (
          <div key={park.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', background: '#fff', border: '1px solid #e8e4de', marginBottom: '6px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#12100e', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{park.name}</p>
              <div style={{ height: '2px', background: '#f0ece6', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${pct}%`, background: GOLD, transition: 'width 0.3s' }} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              <button onClick={() => onChange(park.id, Math.max(1, nights - 1))} disabled={nights <= 1}
                style={{ width: '28px', height: '28px', border: '1px solid #ddd9d2', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: nights <= 1 ? 'default' : 'pointer', opacity: nights <= 1 ? 0.3 : 1, color: '#12100e' }}>
                <Minus size={12} strokeWidth={2} />
              </button>
              <div style={{ width: '52px', textAlign: 'center', border: '1px solid #e2ddd6', padding: '4px 0' }}>
                <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '27px', fontWeight: 300, color: '#0f172a', lineHeight: 1, display: 'block' }}>{nights}</span>
                <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.14em', color: '#12100e' }}>nights</span>
              </div>
              <button onClick={() => onChange(park.id, Math.min(max, nights + 1))} disabled={nights >= max || totalAllocated >= totalNights}
                style={{ width: '28px', height: '28px', border: '1px solid #ddd9d2', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: (nights >= max || totalAllocated >= totalNights) ? 'default' : 'pointer', opacity: (nights >= max || totalAllocated >= totalNights) ? 0.3 : 1, color: '#12100e' }}>
                <Plus size={12} strokeWidth={2} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ══════════════════════════════════════
   PER-COUNTRY SECTION
   Parks → lodge fetch → AccommodationCards + Experiences
   Serengeti zones fetched from DB via GET_SERENGETI_ZONES.
══════════════════════════════════════ */
const CountryParkSection = ({
  leg, accentColor,
  selectedParks, onTogglePark,
  parkNights, onParkNightChange,
  accommodation, onAccommodationChange,
  expData, expLoading,
  selectedAttractions, onToggleAttraction,
  experienceCategories, customMoments,
  onDeleteMoment, onOpenMomentForm,
  addingMomentFor, momentDraft, onMomentDraftChange,
  onCommitMoment, onCancelMoment,
  travelMonth = null, selectedSerengetiZones, onSerengetiZonesChange,
}) => {
  const countrySlug  = leg.name.toLowerCase().replace(/\s+/g, '-');
  const selectedList = useMemo(() => selectedParks || [], [selectedParks]);
  const [expOpen,     setExpOpen]     = useState(false);
  const [lodgeCache,  setLodgeCache]  = useState({});
  const [propsLoading, setPropsLoading] = useState(false);

  // ── Parks query ───────────────────────────────────────────────────────────
  const { data: parksData, loading: parksLoading } = useQuery(GET_PARKS_BY_COUNTRY, {
    variables: { countrySlug }, fetchPolicy: 'cache-first', skip: !countrySlug,
  });

  // ── Imperative lodge fetching via useApolloClient ─────────────────────────
  // useLazyQuery with onCompleted is unreliable in Apollo Client 4 for
  // parallel calls — each call overwrites the shared result variable and
  // onCompleted closes over stale props. useApolloClient + client.query()
  // returns a real promise per call so parallel park fetches all resolve
  // independently and write to lodgeCache correctly.

  const client             = useApolloClient();
  const accommodationRef   = useRef(accommodation);
  const legNameRef         = useRef(leg.name);
  const pendingFetches     = useRef(new Set());

  // Keep refs current on every render so async callbacks always
  // read the latest accommodation state and leg name without stale closures.
  useEffect(() => { accommodationRef.current = accommodation; }, [accommodation]);
  useEffect(() => { legNameRef.current = leg.name; },           [leg.name]);

  const fetchPropsForPark = useCallback(async (parkId) => {
    const pid = String(parkId);
    if (pendingFetches.current.has(pid)) return;
    pendingFetches.current.add(pid);
    setPropsLoading(true);

    try {
      const { data } = await client.query({
        query:       GET_PROPERTIES_BY_PARK,
        variables:   { countrySlug, parkId: pid },
        fetchPolicy: 'network-only',
      });

      if (!data?.propertiesByCountry?.length) return;

      // Group properties by their park ID — matches the lodgeCache lookup key
      const byPark = {};
      data.propertiesByCountry.forEach(prop => {
        const ppid = prop.park?.id;
        if (ppid) {
          if (!byPark[ppid]) byPark[ppid] = [];
          byPark[ppid].push(mapApiProperty(prop));
        }
      });

      setLodgeCache(prev => ({ ...prev, ...byPark }));

      // Validate accommodation now that this park's lodges have arrived.
      // Uses refs so we always read current values, never stale closure state.
      Object.values(byPark).forEach(lodges => {
        if (!lodges.length) return;
        const currentId = accommodationRef.current[parkId];
        const isInCache = lodges.some(l => String(l.id) === String(currentId));
        if (!currentId || !isInCache) {
          // Nothing selected yet, or the GA-suggested property wasn't found
          // in this park's lodge list — fall back to first available lodge.
          onAccommodationChange(parkId, lodges[0].id);
        }
        // If isInCache is true the GA-suggested property loaded correctly —
        // leave the selection as-is so it shows as selected in the UI.
      });

    } catch (err) {
      console.error('[fetchPropsForPark] error for parkId', pid, err);
      // Remove from pending so the user can retry by re-selecting the park
      pendingFetches.current.delete(pid);
    } finally {
      setPropsLoading(false);
    }
  }, [client, countrySlug, onAccommodationChange]);

  // ── Trigger a fetch for each newly selected park ──────────────────────────
  // Runs when selectedList.length changes (park ticked or suggestion applied).
  // pendingFetches guards against duplicate calls for the same park UUID.
  useEffect(() => {
    selectedList.forEach(park => fetchPropsForPark(park.id));
  }, [selectedList.length, fetchPropsForPark]); // eslint-disable-line

  // ── Serengeti zones query — fired when a Serengeti park is selected ───────
  const serengetiPark = selectedList.find(isSerengeti);
  const { data: serengetiZonesData, loading: migrationsLoading } = useQuery(GET_SERENGETI_ZONES, {
    variables: { parkSlug: serengetiPark?.slug || 'serengeti-national-park' },
    skip:        !serengetiPark,
    fetchPolicy: 'cache-first',
  });

  // Map DB zones → UI shape using ZONE_UI_META for display-only metadata.
  // keywords come from DB locationKeywords — same source as backend
  // park_fee_service._resolve_migration_zone(), single source of truth.
  const { data: migrationData } = useQuery(GET_GREAT_MIGRATION, { skip: !serengetiPark, fetchPolicy: 'cache-first' });
  const migrationCalendar = migrationData?.greatMigration || [];
  const herdMonth = travelMonth ? migrationCalendar.find((m) => m.month === travelMonth) : null;

  const migrationZones = useMemo(() => {
    const dbZones = serengetiZonesData?.parkMigrationZones || [];
    // One card per zone key (the zone table can hold two rows that derive
    // the same key), with WHEN the herds are there and whether they are
    // there in the guest's month.
    const seen = new Set();
    const zones = [];
    dbZones.forEach(z => {
      const key  = zoneNameToKey(z.zoneName);
      if (seen.has(key)) return;
      seen.add(key);
      const meta = ZONE_UI_META[key] || { icon: Mountain, color: GOLD };
      const months = migrationCalendar.filter((m) => m.zones.some((hz) => hz.key === key)).map((m) => m.month);
      const herd = herdMonth?.zones.find((hz) => hz.key === key) || null;
      zones.push({
        key,
        label:    z.zoneName,
        sub:      z.notes || '',
        months:   months.length ? `Herds usually here ${monthRanges(months)}` : 'Off the main migration route',
        herd:     herd ? herd.strength : null,         // peak | present | null in the guest's month
        icon:     meta.icon,
        color:    meta.color,
        keywords: Array.isArray(z.locationKeywords) ? z.locationKeywords : [],
      });
    });
    // Zones with the herds this month first, peak before present.
    const rank = (z) => (z.herd === 'peak' ? 0 : z.herd === 'present' ? 1 : 2);
    return zones.sort((a, b) => rank(a) - rank(b));
  }, [serengetiZonesData, migrationCalendar, herdMonth]);

  // ── Park list and region grouping ─────────────────────────────────────────
  const parks = useMemo(() => parksData?.parks || [], [parksData]);

  const regionGroups = useMemo(() => {
    const map = {};
    parks.forEach(park => {
      const rn = park.region?.name || 'Other';
      if (!map[rn]) map[rn] = { name: rn, parks: [] };
      map[rn].parks.push(park);
    });
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
  }, [parks]);

  const [openRegions, setOpenRegions] = useState({});
  useEffect(() => {
    if (regionGroups.length > 0) {
      setOpenRegions(prev => {
        const next = { ...prev };
        regionGroups.forEach(g => {
          if (next[g.name] === undefined) next[g.name] = true;
        });
        return next;
      });
    }
  }, [regionGroups.length]); // eslint-disable-line

  // ── Experiences ───────────────────────────────────────────────────────────
  const available = (expData?.experiencesByCountry || []).map(mapApiExperience);

  const SL = ({ text }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
      <div style={{ width: '24px', height: '1px', background: GOLD }} />
      <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.2em', color: GOLD }}>{text}</span>
    </div>
  );

  return (
    <div style={{ border: '1px solid #e2ddd6', background: '#fff', overflow: 'hidden' }}>
      <div style={{ height: '3px', background: `linear-gradient(90deg, ${accentColor}, rgba(0,0,0,0))` }} />
      <div style={{ padding: '28px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '24px', height: '1px', background: accentColor }} />
            <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '30px', fontWeight: 300, fontStyle: 'normal', color: '#0f172a' }}>{leg.name}</h3>
            <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', color: '#12100e' }}>{leg.nights} nights</span>
          </div>
          {selectedList.length > 0 && (
            <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.16em', color: accentColor }}>
              {selectedList.length} park{selectedList.length > 1 ? 's' : ''} selected
            </span>
          )}
        </div>

        {/* Parks */}
        {parksLoading && <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '15px', color: '#12100e', fontWeight: 300, paddingBottom: '16px' }}>Loading parks…</p>}
        {!parksLoading && parks.length === 0 && (
          <div style={{ padding: '16px', border: '1px solid rgba(184,151,90,0.2)', background: 'rgba(184,151,90,0.03)', marginBottom: '16px' }}>
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '15px', color: '#12100e', fontWeight: 300 }}>No parks listed for {leg.name} yet. Add experiences below.</p>
          </div>
        )}

        {regionGroups.map(group => {
          const isOpen = openRegions[group.name] !== false;
          return (
            <div key={group.name} style={{ marginBottom: '12px', border: '1px solid rgba(184,151,90,0.2)', overflow: 'hidden' }}>
              <button
                onClick={() => setOpenRegions(prev => ({ ...prev, [group.name]: !prev[group.name] }))}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '10px 14px', background: isOpen ? 'linear-gradient(90deg, rgba(184,151,90,0.07), rgba(184,151,90,0.02))' : 'rgba(184,151,90,0.03)', border: 'none', borderBottom: isOpen ? '1px solid rgba(184,151,90,0.15)' : 'none', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <MapPin size={10} strokeWidth={1.5} style={{ color: GOLD }} />
                  <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.2em', color: GOLD }}>{group.name} Region</span>
                  <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', padding: '1px 7px', background: GOLD_DIM, border: `1px solid rgba(184,151,90,0.25)`, color: GOLD }}>{group.parks.length}</span>
                  {(() => { const sc = group.parks.filter(p => selectedList.some(s => s.id === p.id)).length; return sc > 0 ? <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', padding: '1px 7px', background: `${accentColor}18`, border: `1px solid ${accentColor}40`, color: accentColor }}>{sc} selected</span> : null; })()}
                </div>
                {isOpen ? <ChevronUp size={12} strokeWidth={1.5} style={{ color: '#12100e' }} /> : <ChevronDown size={12} strokeWidth={1.5} style={{ color: '#12100e' }} />}
              </button>
              {isOpen && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px', padding: '12px' }}>
                  {group.parks.map(park => (
                    <ParkChip key={park.id} park={park}
                      isSelected={selectedList.some(p => p.id === park.id)}
                      onToggle={onTogglePark} accentColor={accentColor} />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Lodge sections per selected park */}
        {selectedList.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            {selectedList.length > 1 && (
              <ParkNightsSplitter
                selectedParksList={selectedList}
                parkNights={parkNights}
                totalNights={leg.nights}
                onChange={onParkNightChange}
              />
            )}

            {selectedList.map(park => {
              const allLodges  = lodgeCache[park.id] || [];
              const isLoading  = propsLoading && !lodgeCache[park.id];
              const sSerengeti = isSerengeti(park);
              const sZanzibar  = isZanzibar(park);

              return (
                <div key={park.id} style={{ marginTop: '16px', border: '1px solid rgba(184,151,90,0.2)', overflow: 'hidden' }}>
                  <div style={{
                    padding: '12px 16px',
                    background: sSerengeti
                      ? 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(184,151,90,0.04))'
                      : sZanzibar
                        ? 'linear-gradient(135deg, rgba(6,182,212,0.1), rgba(184,151,90,0.04))'
                        : 'linear-gradient(135deg, rgba(184,151,90,0.08), rgba(184,151,90,0.03))',
                    borderBottom: '1px solid rgba(184,151,90,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Hotel size={12} strokeWidth={1.5} style={{ color: sSerengeti ? '#f59e0b' : sZanzibar ? '#06b6d4' : GOLD }} />
                      <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.16em', color: sSerengeti ? '#f59e0b' : sZanzibar ? '#06b6d4' : GOLD }}>
                        {sSerengeti ? 'Migration Lodges' : sZanzibar ? 'Beach Properties' : 'Lodges'} · {park.name}
                      </span>
                      {sSerengeti && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.16em', padding: '2px 7px', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', color: '#f59e0b' }}>Migration-smart</span>}
                    </div>
                    {accommodation[park.id] && allLodges.find(l => String(l.id) === String(accommodation[park.id])) && (
                      <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', padding: '2px 8px', background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.3)', color: '#4ade80' }}>
                        1 selected
                      </span>
                    )}
                  </div>

                  <div style={{ padding: '20px' }}>
                    {isLoading && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {[1, 2, 3].map(i => <div key={i} style={{ height: '16px', background: 'linear-gradient(90deg, #f0ece6 25%, #e8e4de 50%, #f0ece6 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', width: `${50 + i * 15}%` }} />)}
                      </div>
                    )}

                    {!isLoading && allLodges.length === 0 && (
                      <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', color: '#12100e', fontStyle: 'normal' }}>
                        No lodges on file for {park.name} yet. Our team will suggest options.
                      </p>
                    )}

                    {!isLoading && allLodges.length > 0 && sSerengeti && (
                      <SerengetiLodgeSection
                        park={park}
                        allLodges={allLodges}
                        nights={leg.nights}
                        migrationZones={migrationZones}
                        migrationsLoading={migrationsLoading}
                        herdMonth={herdMonth}
                        monthName={travelMonth ? MONTH_FULL[travelMonth - 1] : null}
                        selectedSerengetiZones={selectedSerengetiZones}
                        onSerengetiZonesChange={onSerengetiZonesChange}
                        selectedId={accommodation[park.id]}
                        onSelect={(id) => onAccommodationChange(park.id, id)}
                        accentColor={accentColor}
                      />
                    )}

                    {!isLoading && allLodges.length > 0 && sZanzibar && (
                      <ZanzibarLodgeSection
                        allLodges={allLodges} nights={leg.nights}
                        selectedId={accommodation[park.id]}
                        onSelect={(id) => onAccommodationChange(park.id, id)}
                        accentColor={accentColor}
                      />
                    )}

                    {!isLoading && allLodges.length > 0 && !sSerengeti && !sZanzibar && (
                      <ProximityGroupedCards
                        props={allLodges} nights={leg.nights}
                        selectedId={accommodation[park.id]}
                        onSelect={(id) => onAccommodationChange(park.id, id)}
                        accentColor={accentColor}
                      />
                    )}

                    {accommodation[park.id] && (() => {
                      const sel = allLodges.find(l => String(l.id) === String(accommodation[park.id]));
                      return sel ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0ece6' }}>
                          <Check size={12} strokeWidth={2} style={{ color: '#4ade80' }} />
                          <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', color: '#4ade80' }}>
                            Selected: <strong style={{ color: '#12100e' }}>{sel.name}</strong>
                          </span>
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Experiences (collapsible) ── */}
        <div style={{ marginTop: '24px', borderTop: '1px solid #f0ece6', paddingTop: '20px' }}>
          <button
            onClick={() => setExpOpen(o => !o)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', paddingBottom: expOpen ? '16px' : '0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Camera size={13} strokeWidth={1.5} style={{ color: GOLD }} />
              <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.2em', color: GOLD }}>Experiences & Activities</span>
              {selectedAttractions.length > 0 && (
                <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', padding: '2px 8px', background: GOLD_DIM, border: `1px solid rgba(184,151,90,0.3)`, color: GOLD }}>{selectedAttractions.length} added</span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.14em', color: '#12100e' }}>{expOpen ? 'collapse' : 'expand'}</span>
              {expOpen ? <ChevronUp size={13} strokeWidth={1.5} style={{ color: '#12100e' }} /> : <ChevronDown size={13} strokeWidth={1.5} style={{ color: '#12100e' }} />}
            </div>
          </button>

          {expOpen && (
            <div>
              {expLoading && <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '15px', color: '#12100e', fontWeight: 300 }}>Loading experiences…</p>}
              {!expLoading && available.length === 0 && <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '15px', color: '#12100e', fontWeight: 300, fontStyle: 'normal' }}>No curated experiences for {leg.name} yet.</p>}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '10px', marginBottom: '12px' }}>
                {available.map(attr => {
                  const Icon  = attr.icon;
                  const isSel = !!selectedAttractions.find(a => a.id === attr.id);
                  return (
                    <button key={attr.id} onClick={() => onToggleAttraction(leg, attr)}
                      style={{ textAlign: 'left', padding: '14px', border: `1px solid ${isSel ? GOLD : '#e2ddd6'}`, background: isSel ? GOLD_DIM : '#fff', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(0.22,1,0.36,1)', position: 'relative', overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <div style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${isSel ? GOLD : '#e2ddd6'}`, flexShrink: 0 }}>
                          <Icon size={12} strokeWidth={1.5} style={{ color: isSel ? GOLD : '#64748b' }} />
                        </div>
                        {isSel && <Check size={12} strokeWidth={2.5} style={{ color: GOLD }} />}
                      </div>
                      <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '18px', fontWeight: 300, fontStyle: 'normal', color: '#0f172a', lineHeight: 1.1, marginBottom: '4px' }}>{attr.name}</p>
                      <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 300, color: '#12100e', lineHeight: 1.4, marginBottom: '8px' }}>{attr.desc}</p>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', color: '#12100e', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Clock size={8} strokeWidth={1.5} /> {attr.duration}
                        </span>
                        {attr.priceOnRequest
                          ? <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: GOLD }}>On request</span>
                          : <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '18px', fontWeight: 300, color: '#0f172a' }}>${attr.price}<span style={{ fontSize: '13px', color: '#12100e' }}>/pp</span></span>
                        }
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Custom moments */}
              {(customMoments[leg.name] || []).map(cm => {
                const isSel = !!selectedAttractions.find(a => a.id === cm.id);
                return (
                  <div key={cm.id} style={{ border: `1px solid ${isSel ? GOLD : 'rgba(184,151,90,0.3)'}`, background: isSel ? GOLD_DIM : 'rgba(184,151,90,0.04)', marginBottom: '6px', overflow: 'hidden' }}>
                    <button onClick={() => onToggleAttraction(leg, cm)} style={{ width: '100%', textAlign: 'left', padding: '12px 14px', background: 'none', border: 'none', cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontFamily: 'Jost, sans-serif', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.2em', padding: '2px 7px', background: NAVY, color: GOLD }}>
                            <Sparkles size={7} strokeWidth={2} />Custom
                          </span>
                          <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '18px', fontWeight: 300, fontStyle: 'normal', color: '#0f172a' }}>{cm.name}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {isSel && <Check size={11} strokeWidth={2.5} style={{ color: GOLD }} />}
                          <button onClick={e => { e.stopPropagation(); onDeleteMoment(leg.name, cm.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '17px' }}>×</button>
                        </div>
                      </div>
                    </button>
                  </div>
                );
              })}

              {addingMomentFor === leg.name ? (
                <div style={{ border: '1px dashed rgba(184,151,90,0.4)', background: 'rgba(184,151,90,0.04)', padding: '16px', marginTop: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.16em', color: GOLD }}>Define your moment · {leg.name}</span>
                    <button onClick={onCancelMoment} style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.14em', color: '#12100e', background: 'none', border: 'none', cursor: 'pointer' }}>Cancel</button>
                  </div>
                  <input autoFocus placeholder="e.g. Sunrise hot-air balloon over the Mara…"
                    value={momentDraft.name} onChange={e => onMomentDraftChange({ ...momentDraft, name: e.target.value })}
                    style={{ width: '100%', background: 'transparent', borderBottom: '1px solid #ddd9d2', outline: 'none', fontFamily: 'Jost, sans-serif', fontSize: '15px', fontWeight: 300, color: '#0f172a', paddingBottom: '8px', marginBottom: '12px' }} />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={onCancelMoment} style={{ padding: '8px 14px', border: '1px solid #ddd9d2', background: '#fff', fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.16em', cursor: 'pointer', color: '#12100e' }}>Cancel</button>
                    <button onClick={() => onCommitMoment(leg.name)} disabled={!momentDraft.name.trim()}
                      style={{ flex: 1, padding: '8px 14px', background: NAVY, color: '#fff', border: `1px solid ${NAVY}`, fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.16em', cursor: momentDraft.name.trim() ? 'pointer' : 'default', opacity: momentDraft.name.trim() ? 1 : 0.4 }}>
                      Add to {leg.name}
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => onOpenMomentForm(leg.name)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', border: '1px dashed rgba(184,151,90,0.35)', background: 'transparent', padding: '12px', fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(184,151,90,0.7)', cursor: 'pointer', marginTop: '8px' }}>
                  <Sparkles size={10} strokeWidth={1.5} /> Define your own moment in {leg.name}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════
   MAIN EXPORT
══════════════════════════════════════ */
const StepParksLodgesExperiences = ({
  itinerary, onNext, onBack,
  selectedParks, onParksChange,
  parkNights, onParkNightsChange,
  accommodation, onAccommodationChange,
  expDataByIndex, expLoadingByIndex,
  customMoments, experienceCategories,
  onToggleAttraction, onCommitMoment, onDeleteCustomMoment,
  selectedSerengetiZones, onSerengetiZonesChange,
  travelMonth = null,
}) => {
  const [addingMomentFor, setAddingMomentFor] = useState(null);
  const [momentDraft, setMomentDraft] = useState({
    name: '', categorySlug: '', duration: '', difficulty: 'Easy', price: 0, desc: '',
  });

  const handleTogglePark = useCallback((countryName, park) => {
    const current = selectedParks[countryName] || [];
    const exists  = current.find(p => p.id === park.id);
    onParksChange(countryName, exists ? current.filter(p => p.id !== park.id) : [...current, park]);
  }, [selectedParks, onParksChange]);

  return (
    <div className="fade-up" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{ marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
          <div style={{ width: '24px', height: '1px', background: GOLD }} />
          <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.2em', color: GOLD }}>
            Step 02 · Parks, Lodges & Experiences
          </span>
        </div>
        <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(32px,3.5vw,40px)', fontStyle: 'normal', fontWeight: 300, color: '#0f172a', lineHeight: 1 }}>
          Choose Your Wild Places
        </h2>
      </div>

      <div style={{ display: 'flex', alignItems: 'start', gap: '10px', padding: '14px 16px', border: '1px solid rgba(184,151,90,0.25)', background: 'rgba(184,151,90,0.05)' }}>
        <Info size={12} strokeWidth={1.5} style={{ color: GOLD, flexShrink: 0, marginTop: '1px' }} />
        <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '15px', fontWeight: 300, color: '#12100e', lineHeight: 1.6 }}>
          Select parks — lodges load automatically for each one. Pick your lodge and any experiences.
          For the Serengeti, choose whether to follow the Great Migration and select your zone(s).
        </p>
      </div>

      {itinerary.map((leg, idx) => (
        <CountryParkSection
          key={leg.name}
          leg={leg}
          accentColor={getCountryColor(leg.name)}
          selectedParks={selectedParks[leg.name] || []}
          onTogglePark={(park) => handleTogglePark(leg.name, park)}
          parkNights={parkNights}
          onParkNightChange={onParkNightsChange}
          accommodation={accommodation}
          onAccommodationChange={onAccommodationChange}
          expData={expDataByIndex?.[idx]}
          expLoading={expLoadingByIndex?.[idx]}
          selectedAttractions={leg.selectedAttractions}
          onToggleAttraction={(leg, attr) => onToggleAttraction(
            itinerary.findIndex(l => l.name === leg.name), attr
          )}
          experienceCategories={experienceCategories}
          customMoments={customMoments}
          onDeleteMoment={onDeleteCustomMoment}
          onOpenMomentForm={(country) => {
            setAddingMomentFor(country);
            setMomentDraft({ name: '', categorySlug: experienceCategories[0]?.slug || '', duration: '', difficulty: 'Easy', price: 0, desc: '' });
          }}
          addingMomentFor={addingMomentFor}
          momentDraft={momentDraft}
          onMomentDraftChange={setMomentDraft}
          onCommitMoment={(country) => { onCommitMoment(country, momentDraft); setAddingMomentFor(null); }}
          onCancelMoment={() => setAddingMomentFor(null)}
          travelMonth={travelMonth}
          selectedSerengetiZones={selectedSerengetiZones}
          onSerengetiZonesChange={onSerengetiZonesChange}
        />
      ))}

      <div style={{ display: 'flex', gap: '12px' }}>
        <button onClick={onBack} className="ghost-cta">
          <ArrowLeft size={12} strokeWidth={1.5} /> Back
        </button>
        <button onClick={onNext} className="primary-cta" style={{ flex: 1 }}>
          <span>Continue to Meal Plan</span>
          <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
};

export default StepParksLodgesExperiences;